import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, Plus, Check, ChevronLeft, ChevronRight, Package, Eye, ShoppingBag, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';

const ToptexCatalog = ({ onImportDone }) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Detail modal
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailPrices, setDetailPrices] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Import
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const fetchCatalog = useCallback(async (p = 1, search = null, brand = null) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (search) params.search = search;
      if (brand) params.brand = brand;
      const res = await api.get('/provider/toptex/catalog', { params });
      setItems(res.data.items || []);
      setTotalCount(res.data.total_count || 0);
      setPage(p);
      setHasSearched(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement catalogue Toptex');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const handleSearch = () => {
    const ref = searchRef.trim().toUpperCase();
    if (ref) {
      fetchCatalog(1, ref, null);
    } else if (brandFilter) {
      fetchCatalog(1, null, brandFilter);
    } else {
      fetchCatalog(1);
    }
  };

  const handleBrowseAll = () => {
    setSearchRef('');
    setBrandFilter('');
    fetchCatalog(1);
  };

  const handlePageChange = (newPage) => {
    if (searchRef.trim()) {
      fetchCatalog(newPage, searchRef.trim().toUpperCase(), null);
    } else if (brandFilter) {
      fetchCatalog(newPage, null, brandFilter);
    } else {
      fetchCatalog(newPage);
    }
  };

  const handleViewDetail = async (ref) => {
    setDetailLoading(true);
    setDetailProduct(null);
    setDetailPrices([]);
    try {
      const [prodRes, priceRes] = await Promise.all([
        api.get(`/provider/toptex/product/${ref}`),
        api.get(`/provider/toptex/prices/${ref}`).catch(() => ({ data: { prices: [] } })),
      ]);
      setDetailProduct(prodRes.data.product);
      setDetailPrices(priceRes.data.prices || []);
    } catch (e) {
      toast.error('Erreur chargement detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleSelect = (ref) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
  };

  const handleImportSelected = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const productsToImport = items.filter(i => selected.has(i.ref));
      const res = await api.post('/provider/toptex/import', { products: productsToImport });
      toast.success(res.data.message);
      setSelected(new Set());
      if (onImportDone) onImportDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  const handleImportSingle = async (product) => {
    try {
      const res = await api.post('/provider/toptex/import', { products: [product] });
      toast.success(res.data.message);
      if (onImportDone) onImportDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    }
  };

  // Get min price from price list for a ref
  const getMinPrice = (ref) => {
    if (!detailPrices.length) return null;
    const refPrices = detailPrices.filter(p => p.catalogReference === ref || p.sku?.startsWith(ref));
    if (!refPrices.length) return null;
    let min = Infinity;
    for (const p of refPrices) {
      for (const pr of (p.prices || [])) {
        const v = parseFloat(pr.price);
        if (v < min) min = v;
      }
    }
    return min === Infinity ? null : min;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div data-testid="toptex-catalog-section">
      <h3 className="font-heading font-bold text-base uppercase mb-4">Catalogue Toptex API</h3>
      <p className="text-xs text-slate-500 mb-4">
        Parcourez le catalogue Toptex en temps reel ({totalCount > 0 ? `${totalCount} produits disponibles` : 'connecte via API'}). Selectionnez et importez les produits dans votre boutique.
      </p>

      {/* Search bar */}
      <div className="bg-white border border-slate-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value.toUpperCase())}
              placeholder="Ref. catalogue (ex: PA4045, K356...)"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="font-mono text-sm h-10"
              data-testid="toptex-search-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2 h-10"
              onClick={handleSearch}
              disabled={loading}
              data-testid="toptex-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Rechercher
            </Button>
            <Button variant="outline" className="h-10 text-xs font-heading uppercase" onClick={handleBrowseAll} disabled={loading} data-testid="toptex-browse-all-btn">
              <ShoppingBag className="w-4 h-4 mr-1" /> Tout parcourir
            </Button>
          </div>
        </div>
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-brand/10 border border-brand/30 p-3 mb-4 flex items-center justify-between">
          <span className="text-sm font-heading font-bold text-brand">{selected.size} produit(s) selectionne(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setSelected(new Set())}>
              <X className="w-3 h-3 mr-1" /> Annuler
            </Button>
            <Button
              className="bg-brand hover:bg-brand/90 text-white text-xs font-heading font-bold uppercase gap-1"
              size="sm"
              onClick={handleImportSelected}
              disabled={importing}
              data-testid="toptex-import-selected-btn"
            >
              {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Importer la selection
            </Button>
          </div>
        </motion.div>
      )}

      {/* Results grid */}
      {loading && !items.length ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
          <span className="ml-3 text-sm text-slate-500">Chargement du catalogue Toptex...</span>
        </div>
      ) : !hasSearched ? (
        <div className="text-center py-16 text-slate-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Utilisez la recherche ou cliquez sur "Tout parcourir" pour explorer le catalogue Toptex.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <p className="text-sm">Aucun produit trouve.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
            <AnimatePresence mode="popLayout">
              {items.map((item) => (
                <motion.div
                  key={item.ref}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className={`bg-white border ${selected.has(item.ref) ? 'border-brand ring-2 ring-brand/20' : 'border-slate-200'} overflow-hidden group cursor-pointer transition-all hover:shadow-md`}
                  data-testid={`toptex-product-${item.ref}`}
                >
                  {/* Image */}
                  <div className="relative aspect-square bg-slate-50 overflow-hidden" onClick={() => handleViewDetail(item.ref)}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-12 h-12" /></div>
                    )}
                    <div className="absolute top-2 left-2 flex gap-1">
                      <span className="bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5">{item.ref}</span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Eye className="w-6 h-6 text-white drop-shadow-lg" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    {item.brand && <span className="text-[10px] font-bold text-slate-400 uppercase">{item.brand}</span>}
                    <h4 className="font-heading font-bold text-xs leading-tight line-clamp-2 mt-0.5">{item.name || item.ref}</h4>

                    {/* Colors dots */}
                    {item.colors.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.colors.slice(0, 8).map((c, i) => (
                          <div key={i} className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: c.hex ? `#${c.hex}` : '#ccc' }} title={c.name} />
                        ))}
                        {item.colors.length > 8 && <span className="text-[10px] text-slate-400 self-center">+{item.colors.length - 8}</span>}
                      </div>
                    )}

                    {/* Sizes */}
                    {item.sizes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {item.sizes.slice(0, 6).map(s => <span key={s} className="bg-slate-100 px-1 py-0.5 text-[9px] font-bold">{s}</span>)}
                        {item.sizes.length > 6 && <span className="text-[9px] text-slate-400 self-center">+{item.sizes.length - 6}</span>}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        variant={selected.has(item.ref) ? "default" : "outline"}
                        size="sm"
                        className={`flex-1 text-[10px] h-7 ${selected.has(item.ref) ? 'bg-brand text-white' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item.ref); }}
                        data-testid={`toptex-select-${item.ref}`}
                      >
                        {selected.has(item.ref) ? <Check className="w-3 h-3 mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                        {selected.has(item.ref) ? 'Selectionne' : 'Selectionner'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[10px] h-7 px-2"
                        onClick={() => handleImportSingle(item)}
                        data-testid={`toptex-import-${item.ref}`}
                      >
                        <ShoppingBag className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-4">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || loading}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-heading">
                Page <span className="font-bold">{page}</span> / {totalPages}
                <span className="text-slate-400 ml-2">({totalCount} produits)</span>
              </span>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || loading}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      <Dialog open={!!detailProduct || detailLoading} onOpenChange={() => { setDetailProduct(null); setDetailPrices([]); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : detailProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading uppercase text-sm flex items-center gap-2">
                  <span className="font-mono bg-brand/10 text-brand px-2 py-0.5 text-xs">{detailProduct.ref}</span>
                  {detailProduct.brand && <span className="text-slate-400 text-xs font-normal">{detailProduct.brand}</span>}
                </DialogTitle>
                <DialogDescription className="sr-only">Detail produit Toptex {detailProduct.ref}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Image + info */}
                <div className="flex gap-4">
                  <div className="w-40 h-40 bg-slate-50 border flex-shrink-0 overflow-hidden">
                    {detailProduct.image_url ? (
                      <img src={detailProduct.image_url} alt={detailProduct.name} className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-10 h-10" /></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-sm">{detailProduct.name}</h3>
                    {detailProduct.family && <p className="text-xs text-slate-400 mt-0.5">{detailProduct.family}{detailProduct.subfamily ? ` > ${detailProduct.subfamily}` : ''}</p>}
                    {detailProduct.composition && <p className="text-xs text-slate-500 mt-1">{detailProduct.composition}</p>}
                    <p className="text-xs text-slate-500 mt-1 line-clamp-3">{detailProduct.description}</p>

                    {/* Price from API */}
                    {detailPrices.length > 0 && (
                      <div className="mt-2">
                        <span className="font-heading font-black text-xl text-brand">
                          {(() => {
                            const p = getMinPrice(detailProduct.ref);
                            return p ? `${p.toFixed(2)}€` : '';
                          })()}
                        </span>
                        <span className="text-[10px] text-slate-400 ml-1">HT / unite (prix degressif)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Colors with packshots */}
                {detailProduct.colors.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-xs uppercase mb-2">Couleurs ({detailProduct.colors.length})</h4>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {detailProduct.colors.map((c, i) => (
                        <div key={i} className="text-center">
                          <div className="w-full aspect-square bg-slate-50 border overflow-hidden mb-1">
                            {c.packshot ? (
                              <img src={c.packshot} alt={c.name} className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: c.hex ? `#${c.hex}` : '#ccc' }} />
                              </div>
                            )}
                          </div>
                          <span className="text-[9px] text-slate-500 leading-tight block">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sizes */}
                {detailProduct.sizes.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-xs uppercase mb-2">Tailles ({detailProduct.sizes.length})</h4>
                    <div className="flex flex-wrap gap-1">
                      {detailProduct.sizes.map(s => <span key={s} className="bg-slate-100 px-2 py-1 text-xs font-bold">{s}</span>)}
                    </div>
                  </div>
                )}

                {/* Price table */}
                {detailPrices.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-xs uppercase mb-2">Prix par taille/couleur (extrait)</h4>
                    <div className="max-h-48 overflow-y-auto border">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-heading">Couleur</th>
                            <th className="text-left p-2 font-heading">Taille</th>
                            <th className="text-right p-2 font-heading">Prix HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPrices.slice(0, 20).map((p, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2">{p.color || '-'}</td>
                              <td className="p-2">{p.size || '-'}</td>
                              <td className="p-2 text-right font-mono font-bold">
                                {p.prices?.[0]?.price ? `${p.prices[0].price}€` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {detailPrices.length > 20 && <p className="text-center text-[10px] text-slate-400 py-1">... et {detailPrices.length - 20} autres variantes</p>}
                    </div>
                  </div>
                )}

                {/* Import button */}
                <Button
                  className="w-full bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2"
                  onClick={() => { handleImportSingle(detailProduct); setDetailProduct(null); }}
                  data-testid="toptex-detail-import-btn"
                >
                  <Plus className="w-4 h-4" /> Importer ce produit dans mon catalogue
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToptexCatalog;
